import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import User from '../../models/general/user';
import CustomInfoWithLabel from '@common/components/misc/CustomInfoWithLabel';
import useValidatedSelectEffect from '../../hooks/useValidatedSelectEffect';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectOrganizationUsersData } from '../../store/organization/organization-slice-selectors';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import { resetUsersRestState } from '../../store/organization/organization-slice';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import SelectInput from '../forms/SelectInput';
import { MoveRightArrow } from '@common/utils/icons/icons';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import UserRole from '../../models/enums/user-roles';
import { patchUserById } from '../../store/organization/organization-actions';
import UserPatchType from '../../models/enums/user-patch-type';

const ChangeOwnerDialog: React.FC<{ user: User; open: boolean; onClose: () => void }> = ({
  user,
  open,
  onClose,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const usersData = useAppSelector((state) => selectOrganizationUsersData(state));

  const { resetToggle, updateResetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      patchError: usersData.rest.patchError,
      resetPatchHandler: () =>
        dispatch(resetUsersRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
    });

  useEffect(() => {
    if (usersData.rest.patchStatus === HttpFetchStatus.Success) {
      onClose();
    }
  }, [onClose, usersData.rest.patchStatus]);

  const transferOptions = [...(usersData.data ?? [])].filter(
    (u) => u.id !== user.id && u.organization.id === user.organization.id,
  );

  const {
    value: userId,
    validationResult: userIdValidationResult,
    valueChangeHandler: userIdChangeHandler,
  } = useValidatedSelectEffect(
    transferOptions.length > 0 ? transferOptions[0].id : '',
    (newValue) => ({ isValid: true, error: null }),
    (_) => () => {},
    getServerValidationResultOrNull('role'),
    removeServerError,
    resetToggle,
  );

  const changeOwnerClickHandler = () => {
    dispatch(
      patchUserById({
        id: userId,
        data: { role: UserRole.OWNER },
        patchType: UserPatchType.UpdateOwner,
      }),
    );
  };

  const canTransfer = user.role === UserRole.OWNER && transferOptions.length > 0;

  const closeHandler = () => {
    updateResetToggle();
    onClose();
  };

  return (
    <React.Fragment>
      <Dialog open={open} onClose={closeHandler} fullWidth maxWidth="sm">
        <DialogTitle>{t('title-change-owner')}</DialogTitle>
        <DialogContent>
          {user.role !== UserRole.OWNER ? (
            <CustomAlert header={t('warning-cannot-change-owner')} severity="warning" />
          ) : transferOptions.length === 0 ? (
            <CustomAlert header={t('warning-cannot-change-owner-as-no-users')} severity="warning" />
          ) : (
            <Grid container pt={1}>
              <Grid item xs={5} pl={2}>
                <CustomInfoWithLabel label={t('info-label-transfer-from')} text={user.name} />
              </Grid>
              <Grid item xs={2} alignItems="center" display="flex" justifyContent="space-around">
                <MoveRightArrow fontSize="large" color="primary" />
              </Grid>
              <Grid item xs={5}>
                <SelectInput
                  data-testid="transfer-to-select"
                  label={t('input-label-transfer-to')}
                  value={userId}
                  onChange={userIdChangeHandler}
                  validationResult={userIdValidationResult}
                >
                  {transferOptions.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name}
                    </MenuItem>
                  ))}
                </SelectInput>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant={canTransfer ? 'text' : 'contained'} onClick={closeHandler}>
            {canTransfer ? t('button-cancel') : t('button-close')}
          </Button>
          {canTransfer && (
            <Button
              variant="contained"
              onClick={changeOwnerClickHandler}
              data-testid="button-change-owner"
              disabled={usersData.rest.patchStatus === HttpFetchStatus.Loading}
            >
              {usersData.rest.patchStatus === HttpFetchStatus.Loading ? (
                <CircularProgress size={24} />
              ) : (
                t('button-change-owner')
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ChangeOwnerDialog;
